import type { LocalizedText } from "../i18n/i18n";

export type TemplateVariable = {
  /** Token key used in templates: {{key}} */
  key: string;
  /** Human readable label */
  label: LocalizedText;
  /** Optional group name for UI (localized) */
  group: LocalizedText;
  /** Optional editor instruction: insert this variable as a hyperlink using another token as target. */
  link?: {
    targetKey: string;
    hrefPrefix?: string;
    textKey?: string;
    displayText?: LocalizedText;
  };
};

// Internal array - not exported directly to avoid initialization issues
const _defaultTemplateVariablesData: TemplateVariable[] = [
  // Person
  { group: "Customer", key: "title", label: "Title" },
  { group: "Customer", key: "firstName", label: "First name" },
  { group: "Customer", key: "lastName", label: "Last name" },
  { group: "Customer", key: "email", label: "Email" },
  { group: "Customer", key: "emailLink", label: "Email as link", link: { targetKey: "email", hrefPrefix: "mailto:", displayText: "Email link text" } },
  { group: "Customer", key: "phone", label: "Phone" },
  { group: "Customer", key: "mobile", label: "Mobile" },
  { group: "Customer", key: "street", label: "Street" },
  { group: "Customer", key: "zip", label: "ZIP" },
  { group: "Customer", key: "city", label: "City" },
  { group: "Customer", key: "country", label: "Country" },
  { group: "Customer", key: "dateOfBirth", label: "Date of birth" },

  // Anreden (fertig kombiniert)
  { group: "Salutation", key: "greeting", label: "Hello" },
  { group: "Salutation", key: "greetingFirstName", label: "Hello + first name" },
  { group: "Salutation", key: "greetingLastName", label: "Hello + Mr./Ms. last name" },
  { group: "Salutation", key: "salutationDearFirstName", label: "Dear + first name" },
  { group: "Salutation", key: "salutationDearLastName", label: "Dear + Mr./Ms. last name" },
  { group: "Salutation", key: "salutationFormalFull", label: "Dear Sir/Madam + last name" },

  // Pronomen (Du/Sie-abhängig)
  { group: "Pronouns", key: "duSie", label: "you (nominative)" },
  { group: "Pronouns", key: "dirIhnen", label: "you (dative)" },
  { group: "Pronouns", key: "dichSie", label: "you (accusative)" },
  { group: "Pronouns", key: "deinIhr", label: "your (masculine)" },
  { group: "Pronouns", key: "deineIhre", label: "your (feminine)" },

  // SEPA / Payment basics
  { group: "SEPA", key: "bankAccountHolder", label: "Account holder" },
  { group: "SEPA", key: "iban", label: "IBAN" },
  { group: "SEPA", key: "bic", label: "BIC" },
  { group: "SEPA", key: "mandateReference", label: "Mandate reference" },
  { group: "SEPA", key: "mandateSignedDate", label: "Mandate date" },

  // Studio
  { group: "Studio", key: "studioName", label: "Name" },
  { group: "Studio", key: "studioShortName", label: "Short name" },
  { group: "Studio", key: "studioCode", label: "Code" },
  { group: "Studio", key: "studioStreet", label: "Street" },
  { group: "Studio", key: "studioZip", label: "ZIP" },
  { group: "Studio", key: "studioCity", label: "City" },
  { group: "Studio", key: "studioAddress", label: "Address in one line" },
  { group: "Studio", key: "studioCountry", label: "Country" },
  { group: "Studio", key: "studioPhone", label: "Phone" },
  { group: "Studio", key: "studioEmail", label: "Email" },
  { group: "Studio", key: "studioEmailLink", label: "Email as link", link: { targetKey: "studioEmail", hrefPrefix: "mailto:", displayText: "Email link text" } },
  { group: "Studio", key: "studioWebsite", label: "Website" },
  { group: "Studio", key: "studioWebsiteLink", label: "Website as link", link: { targetKey: "studioWebsite", displayText: "Website link text" } },
  { group: "Studio", key: "studioCostCenter", label: "Cost center" },
  { group: "Studio", key: "studioBankAccountHolder", label: "Account holder" },
  { group: "Studio", key: "studioIban", label: "IBAN" },
  { group: "Studio", key: "studioBic", label: "BIC" },

  // Firma — die Pflichtangaben des Mandanten. Sie waren zuvor nur über die Feldauswahl des
  // Fußbereichs erreichbar; ein frei geschriebener Fußtext braucht sie als Variablen.
  { group: "Company", key: "companyName", label: "Company legal name" },
  { group: "Company", key: "companyStreet", label: "Street" },
  { group: "Company", key: "companyZip", label: "ZIP" },
  { group: "Company", key: "companyCity", label: "City" },
  { group: "Company", key: "companyAddress", label: "Address in one line" },
  { group: "Company", key: "companyPhone", label: "Phone" },
  { group: "Company", key: "companyEmail", label: "Email" },
  { group: "Company", key: "companyWebsite", label: "Website" },
  { group: "Company", key: "companyRepresentatives", label: "Legal representatives" },
  { group: "Company", key: "companyRepresentativesLabel", label: "Designation of the representatives" },
  { group: "Company", key: "companySupervisoryBoardChair", label: "Supervisory board chair" },
  { group: "Company", key: "companyRegisterCourt", label: "Register court" },
  { group: "Company", key: "companyRegisterNumber", label: "Register number" },
  { group: "Company", key: "companyRegister", label: "Register court and number" },
  { group: "Company", key: "companyTaxNumber", label: "Tax number" },
  { group: "Company", key: "companyVatId", label: "VAT ID" },

  // Vertrag
  { group: "Contract", key: "contractPlanName", label: "Contract name/Plan" },
  { group: "Contract", key: "contractStartDate", label: "Contract start date" },
  { group: "Contract", key: "contractEndDate", label: "Contract end date" },
  { group: "Contract", key: "contractStatus", label: "Contract status" },
  { group: "Contract", key: "contractPrice", label: "Contract price" },
  { group: "Contract", key: "contractMainTermTotalAmount", label: "Main term total amount" },
  { group: "Contract", key: "contractPaidAmount", label: "Paid contract amount" },
  { group: "Contract", key: "contractRemainingAmount", label: "Remaining contract amount" },
  { group: "Contract", key: "contractBillingCycle", label: "Payment cycle" },
  { group: "Contract", key: "contractTerm", label: "Term" },
  { group: "Contract", key: "contractTermValue", label: { de: "Laufzeit (nur Zahl)", en: "Term (number only)" } },
  { group: "Contract", key: "contractTermUnit", label: { de: "Laufzeit-Einheit (Tage/Wochen/Monate)", en: "Term unit (days/weeks/months)" } },
  { group: "Contract", key: "contractItems", label: { de: "Positionen des Vertragspakets (eine je Zeile)", en: "Contract package items (one per line)" } },
  { group: "Contract", key: "contractBundleNames", label: { de: "gebuchte Leistungsbündel", en: "Booked service bundles" } },
  { group: "Contract", key: "contractBundlePriceTotal", label: { de: "Summe der Bündelpreise", en: "Total bundle price" } },
  { group: "Contract", key: "contractBundleBenefitTotal", label: { de: "Summe der Bündelvorteile", en: "Total bundle benefit" } },
  { group: "Contract", key: "contractPromotionNames", label: { de: "angewandte Verkaufsaktionen", en: "Applied sales promotions" } },
  { group: "Contract", key: "contractPromotionEffects", label: { de: "eingefrorene Wirkung der Verkaufsaktionen", en: "Frozen sales promotion effects" } },

  // Rechtliches (Vertragsdokumente)
  { group: { de: "Rechtliches", en: "Legal" }, key: "Widerrufsbelehrung", label: { de: "Widerrufsbelehrung", en: "Withdrawal policy" } },
  { group: { de: "Rechtliches", en: "Legal" }, key: "Widerrufsformular", label: { de: "Muster-Widerrufsformular", en: "Sample withdrawal form" } },

  // Kündigung
  { group: "Cancellation", key: "cancellationDate", label: "Cancellation date" },
  { group: "Cancellation", key: "cancellationReason", label: "Cancellation reason" },
  { group: "Cancellation", key: "cancellationNote", label: "Cancellation note" },
  { group: "Cancellation", key: "cancellationProcessedBy", label: "Cancellation processed by" },

  // Ruhezeit
  { group: "Rest period", key: "restPeriodRequestDate", label: "Rest period request date" },
  { group: "Rest period", key: "restPeriodStartDate", label: "Rest period start date" },
  { group: "Rest period", key: "restPeriodEndDate", label: "Rest period end date" },
  { group: "Rest period", key: "restPeriodMonthlyFee", label: "Rest period monthly fee" },
  { group: "Rest period", key: "restPeriodFeeTotalAmount", label: "Rest period total fee amount" },
  { group: "Rest period", key: "restPeriodNextContributionDueDate", label: "Rest period next contribution due date" },
  { group: "Rest period", key: "restPeriodNextContributionAmount", label: "Rest period next contribution amount" },
  { group: "Rest period", key: "restPeriodWeeksCount", label: "Rest period weeks count" },
  { group: "Rest period", key: "restPeriodNote", label: "Rest period note" },

  // Datum & Zeit
  { group: "Date & Time", key: "currentDate", label: "Current date" },
  { group: "Date & Time", key: "currentTime", label: "Current time" },
  { group: "Date & Time", key: "currentDateTime", label: "Current date & time" },
  { group: "Date & Time", key: "currentYear", label: "Current year" },
  { group: "Date & Time", key: "currentMonth", label: "Current month" },
  { group: "Date & Time", key: "currentDay", label: "Current day" },

  // Termin (Erinnerung/Bestätigung)
  { group: "Appointment", key: "appointmentStartTime", label: "Appointment start (date + time)" },
  { group: "Appointment", key: "appointmentEndTime", label: "Appointment end (date + time)" },
  { group: "Appointment", key: "appointmentDate", label: "Appointment date" },
  { group: "Appointment", key: "appointmentTime", label: "Appointment time" },
  { group: "Appointment", key: "appointmentName", label: "Appointment name" },
  { group: "Appointment", key: "appointmentTypeName", label: "Appointment type name" },
  { group: "Appointment", key: "appointmentDurationMinutes", label: "Duration in minutes" },
  { group: "Appointment", key: "bookingReference", label: "Booking reference" },
  { group: "Appointment", key: "guestFirstName", label: "Guest first name" },
  { group: "Appointment", key: "guestLastName", label: "Guest last name" },
  { group: "Appointment", key: "editBookingUrl", label: "Edit booking link" },
  { group: "Appointment", key: "editBookingLink", label: "Edit booking as link", link: { targetKey: "editBookingUrl", displayText: "Edit booking link text" } },
  { group: "Appointment", key: "bookingConfirmationCode", label: "One-time booking confirmation code (5 digits)" },

  // Mahnung (Reminder / Offener Posten)
  { group: "Reminder", key: "reminder.openItem.amount", label: { de: "Betrag des offenen Postens", en: "Open item amount" } },
  { group: "Reminder", key: "reminder.openItem.dueDate", label: { de: "Fälligkeitsdatum des offenen Postens", en: "Open item due date" } },
  { group: "Reminder", key: "reminder.openItem.description", label: { de: "Beschreibung des offenen Postens", en: "Open item description" } },
  { group: "Reminder", key: "reminder.principalAmount", label: { de: "Summe der gemahnten Hauptposten", en: "Reminder principal amount" } },
  { group: "Reminder", key: "reminder.feeAmount", label: { de: "Summe der Mahngebühren", en: "Reminder fee amount" } },
  { group: "Reminder", key: "reminder.totalAmount", label: { de: "Gesamtsumme des Mahnvorgangs", en: "Reminder total amount" } },
  { group: "Reminder", key: "reminder.regularOpenAmount", label: { de: "regulär offener Betrag", en: "Regular open amount" } },
  { group: "Reminder", key: "reminder.chargebackOpenAmount", label: { de: "offener Rücklastschriftbetrag", en: "Open chargeback amount" } },
  { group: "Reminder", key: "reminder.chargebackFeeOpenAmount", label: { de: "offene Rücklastschriftgebühren", en: "Open chargeback fee amount" } },
  { group: "Reminder", key: "reminder.personTotalOpenAmount", label: { de: "Gesamtsumme der offenen Posten der Person", en: "Person total open amount" } },

  // Inkasso (Übergabeschreiben an den Dienstleister)
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.name", label: { de: "Name des Dienstleisters", en: "Agency name" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.street", label: { de: "Straße des Dienstleisters", en: "Agency street" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.zip", label: { de: "PLZ des Dienstleisters", en: "Agency ZIP" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.city", label: { de: "Ort des Dienstleisters", en: "Agency city" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.country", label: { de: "Land des Dienstleisters", en: "Agency country" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.contactPerson", label: { de: "Ansprechpartner beim Dienstleister", en: "Agency contact person" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.email", label: { de: "E-Mail des Dienstleisters", en: "Agency email" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.emailLink", label: { de: "E-Mail des Dienstleisters als Link", en: "Agency email as link" }, link: { targetKey: "debtCollection.agency.email", hrefPrefix: "mailto:", displayText: "Email link text" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.phone", label: { de: "Telefon des Dienstleisters", en: "Agency phone" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.website", label: { de: "Webseite des Dienstleisters", en: "Agency website" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.agency.customerNumber", label: { de: "eigene Kundennummer beim Dienstleister", en: "Own customer number at the agency" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.handover.transmissionReference", label: { de: "Übermittlungskennung der Übergabe", en: "Transmission reference" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.case.externalReference", label: { de: "Aktenzeichen beim Dienstleister", en: "Agency file reference" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.totalAmount", label: { de: "übergebene Gesamtforderung", en: "Total amount handed over" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.openItems", label: { de: "Aufstellung der Einzelforderungen (eine je Zeile)", en: "List of individual claims (one per line)" } },
  { group: { de: "Inkasso", en: "Debt collection" }, key: "debtCollection.openItemCount", label: { de: "Anzahl der Einzelforderungen", en: "Number of individual claims" } },

  // Vertrags-Widget Abschluss-E-Mail (Termin)
  { group: "Contract widget", key: "BookingWidgetUrl", label: "Booking widget link" },
  { group: "Contract widget", key: "BookingWidgetLink", label: "Booking widget as link", link: { targetKey: "BookingWidgetUrl", displayText: "Booking widget link text" } },
  { group: "Contract widget", key: "TerminBuchungLink", label: "Appointment booking link" },
  { group: "Contract widget", key: "TerminBuchungUrlLink", label: "Appointment booking as link", link: { targetKey: "TerminBuchungLink", displayText: "Appointment booking link text" } },
];

/**
 * Briefarten, für die eine Variable angeboten wird. Ohne diese Einschränkung stünden im Mahnbrief
 * Ruhezeit-Variablen und im Kündigungsschreiben Termin-Variablen — die Auswahlliste wäre
 * unbrauchbar lang. Spiegelt TemplateVariableService.TemplateVariableScopes im Backend.
 */
export type TemplateVariableScope =
  | "common"
  | "reminder"
  | "debtCollection"
  | "cancellation"
  | "restPeriod"
  | "contractConfirmation"
  | "appointment";

/**
 * Zuordnung Gruppe → Briefarten, gespiegelt aus dem Backend (ScopesByGroupEn). Bewusst an der
 * Gruppe statt an jeder Variablen: neue Variablen erben die Zuordnung. Eine unbekannte Gruppe gilt
 * als "common", damit eine vergessene Eintragung nichts versteckt.
 */
const SCOPES_BY_GROUP_EN: Record<string, TemplateVariableScope[]> = {
  Customer: ["common"],
  Salutation: ["common"],
  Pronouns: ["common"],
  SEPA: ["common"],
  Studio: ["common"],
  "Date & Time": ["common"],
  Contract: ["common"],
  Legal: ["contractConfirmation"],
  Cancellation: ["cancellation"],
  "Rest period": ["restPeriod"],
  Appointment: ["appointment"],
  // Mahn-Variablen gelten auch im Inkasso-Übergabeschreiben: dort steht die Forderung, um die es geht.
  Reminder: ["reminder", "debtCollection"],
  "Debt collection": ["debtCollection"],
  "Contract widget": ["contractConfirmation"]
};

function groupKeyEn(group: LocalizedText): string {
  return typeof group === "string" ? group : group.en;
}

export function getTemplateVariableScopes(variable: TemplateVariable): TemplateVariableScope[] {
  return SCOPES_BY_GROUP_EN[groupKeyEn(variable.group)] ?? ["common"];
}

/**
 * Variablen für eine Briefart: die immer gültigen plus die der Briefart. Ohne Angabe bleibt der
 * volle Pool, damit noch nicht umgestellte Aufrufstellen unverändert funktionieren.
 */
export function filterTemplateVariablesByScope(
  variables: readonly TemplateVariable[],
  scope?: TemplateVariableScope | null
): TemplateVariable[] {
  if (!scope) return [...variables];
  return variables.filter((variable) => {
    const scopes = getTemplateVariableScopes(variable);
    return scopes.includes("common") || scopes.includes(scope);
  });
}

// Export as getter function to avoid initialization order issues
let _cachedDefaultTemplateVariables: TemplateVariable[] | null = null;

export function getDefaultTemplateVariables(): TemplateVariable[] {
  if (_cachedDefaultTemplateVariables === null) {
    _cachedDefaultTemplateVariables = _defaultTemplateVariablesData;
  }
  return _cachedDefaultTemplateVariables;
}

// For backward compatibility: export as const that calls the getter
export const defaultTemplateVariables: TemplateVariable[] = getDefaultTemplateVariables();

/**
 * Variablen, die von den heutigen SMS-/WhatsApp-Consumern tatsächlich aufgebaut werden.
 * Personen-/Studiofelder gelten allgemein; Terminfelder nur in Terminbestätigungen und
 * -erinnerungen. Fachvariablen aus Vertrag, Zahlung oder Mahnung werden hier bewusst nicht
 * angeboten, weil die Nachrichten-Renderer dafür keinen Kontext garantieren.
 */
const MESSAGE_TEMPLATE_VARIABLE_KEYS = new Set([
  "firstName", "lastName", "email",
  "studioName", "studioShortName", "studioPhone", "studioEmail",
  "currentDate", "currentTime",
  "appointmentStartTime", "appointmentEndTime", "appointmentDate", "appointmentTime",
  "appointmentName", "appointmentTypeName", "appointmentDurationMinutes", "bookingReference",
  "guestFirstName", "guestLastName", "cancelBookingUrl", "editBookingUrl",
]);

export const messageTemplateVariables: TemplateVariable[] = defaultTemplateVariables.filter(
  (variable) => MESSAGE_TEMPLATE_VARIABLE_KEYS.has(variable.key)
);

/**
 * Variablen der Seitenangabe. Sie stehen bewusst **nicht** im allgemeinen Pool: Eine Seitenzahl
 * gibt es nur dort, wo etwas in Seiten umbricht — im Brief-PDF. In einer Mail oder einem
 * Fließtextfeld wäre sie eine Variable, die nie einen Wert bekommt.
 */
export const pageNumberTemplateVariables: TemplateVariable[] = [
  { group: "Document", key: "pageNumber", label: "Current page" },
  { group: "Document", key: "pageCount", label: "Total pages" },
];

/** Variablen für System-E-Mail-Vorlagen (z. B. Passwort-Reset). Eigener Pool – keine Mandanten-Variablen. */
export const systemEmailTemplateVariables: TemplateVariable[] = [
  { group: "System", key: "resetLink", label: "Reset link URL" },
  { group: "System", key: "resetLinkAsLink", label: "Reset link as link", link: { targetKey: "resetLink", displayText: "Reset link text" } },
  { group: "System", key: "displayName", label: "Display name" },
  { group: "System", key: "username", label: "Username" },
  { group: "System", key: "firstName", label: "First name" },
  { group: "System", key: "lastName", label: "Last name" },
  { group: "Date & Time", key: "currentDate", label: "Current date" },
  { group: "Date & Time", key: "currentTime", label: "Current time" },
  { group: "Date & Time", key: "currentDateTime", label: "Current date & time" },
];

/** Variablen für OTP-Login-Vorlagen (E-Mail mit Einmalcode). */
export const otpLoginTemplateVariables: TemplateVariable[] = [
  { group: "System", key: "otpCode", label: "OTP code (6 digits)" },
  { group: "System", key: "displayName", label: "Display name" },
  { group: "System", key: "email", label: "Email" },
  { group: "Date & Time", key: "currentDate", label: "Current date" },
  { group: "Date & Time", key: "currentTime", label: "Current time" },
  { group: "Date & Time", key: "currentDateTime", label: "Current date & time" },
];

/** Variablen für Termin-Bestätigungsnachricht (Booking-Widget, gleiches Format wie Textversand). */
export const bookingConfirmationVariables: TemplateVariable[] = [
  { group: "Appointment", key: "bookingNumber", label: "Booking number" },
  { group: "Appointment", key: "appointmentTypeName", label: "Appointment type" },
  { group: "Appointment", key: "startTime", label: "Start time" },
  { group: "Appointment", key: "endTime", label: "End time" },
  { group: "Appointment", key: "studioName", label: "Studio" },
  { group: "Appointment", key: "firstName", label: "First name" },
  { group: "Appointment", key: "lastName", label: "Last name" },
  { group: "Appointment", key: "email", label: "Email" },
  { group: "Appointment", key: "emailLink", label: "Email as link", link: { targetKey: "email", hrefPrefix: "mailto:", displayText: "Email link text" } },
  { group: "Appointment", key: "editBookingUrl", label: "Edit booking link" },
  { group: "Appointment", key: "editBookingLink", label: "Edit booking as link", link: { targetKey: "editBookingUrl", displayText: "Edit booking link text" } },
];
